<!-- src/components/MonthlyStatsChart.vue -->
<template>
  <div class="monthly-stats-chart w-full">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>
  
  <script>
  import { ref, onMounted, watch } from 'vue';
  import { Chart, registerables } from 'chart.js';
  
  // Register all Chart.js components
  Chart.register(...registerables);
  
  export default {
    name: 'MonthlyStatsChart',
    props: {
      data: {
        type: Object,
        required: true,
        default: () => ({
          labels: [],
          selections: [],
          wins: [],
          monthlyReturn: [],
          profit: []
        })
      }
    },
    setup(props) {
      const chartCanvas = ref(null);
      let chart = null;
  
      const createChart = () => {
        if (!chartCanvas.value) return;
        
        const ctx = chartCanvas.value.getContext('2d');
        
        // Destroy existing chart if it exists
        if (chart) {
          chart.destroy();
        }
        
        chart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: props.data.labels,
            datasets: [
              {
                label: 'Selections per Month',
                data: props.data.selections,
                backgroundColor: 'rgba(99, 102, 241, 0.6)', // Indigo color (matches shadcn)
                borderColor: 'rgba(99, 102, 241, 1)',
                borderWidth: 1,
                order: 2
              },
              {
                label: 'Wins per Month',
                data: props.data.wins,
                backgroundColor: 'rgba(16, 185, 129, 0.6)', // Green color (matches shadcn)
                borderColor: 'rgba(16, 185, 129, 1)',
                borderWidth: 1,
                order: 1
              },
              {
                label: 'Rolling Profit',
                data: props.data.profit,
                type: 'line',
                backgroundColor: 'rgba(0, 0, 0, 0)',
                borderColor: 'rgba(245, 158, 11, 1)', // Amber color
                borderWidth: 2,
                pointBackgroundColor: 'rgba(245, 158, 11, 1)',
                pointRadius: 3,
                tension: 0.1,
                yAxisID: 'y1',
                order: 0
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Count'
                },
                grid: {
                  display: true,
                  color: 'rgba(0, 0, 0, 0.05)'
                }
              },
              y1: {
                position: 'right',
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Profit (£)'
                },
                grid: {
                  display: false
                },
                ticks: {
                  precision: 0,
                  callback: function(value) {
                    return value.toFixed(0);
                  }
                }
              },
              x: {
                grid: {
                  display: false
                }
              }
            },
            plugins: {
              legend: {
                position: 'top',
                labels: {
                  usePointStyle: true,
                  boxWidth: 6,
                  padding: 15, // Adds space between legend items
                  generateLabels: function(chart) {
                    const originalLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
                    originalLabels.forEach(label => {
                      // Adds space between the colored dot and the text
                      label.text = '  ' + label.text; // 5 spaces for padding
                    });
                    return originalLabels;
                  }
                }
              },
              tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                  afterTitle: (tooltipItems) => {
                    if (tooltipItems.length > 0) {
                      const dataIndex = tooltipItems[0].dataIndex;
                      const monthlyReturn = props.data.monthlyReturn[dataIndex];
                      if (monthlyReturn !== undefined) {
                        const prefix = monthlyReturn >= 0 ? '+' : '';
                        return `Monthly Return: ${prefix}${monthlyReturn}`;
                      }
                    }
                    return '';
                  },
                  label: function(context) {
                    let label = context.dataset.label || '';
                    if (label) {
                      label += ': ';
                    }
                    if (context.parsed.y !== null) {
                      if (context.dataset.label === 'Rolling Profit') {
                        label += context.parsed.y.toFixed(0);
                      } else {
                        label += context.parsed.y;
                      }
                    }
                    return label;
                  }
                }
              }
            }
          }
        });
      };
  
      onMounted(() => {
        createChart();
      });
  
      watch(() => props.data, () => {
        createChart();
      }, { deep: true });
  
      return {
        chartCanvas
      };
    }
  };
  </script>
  
  <style scoped>
  .monthly-stats-chart {
  width: 100%;
  height: 400px;
}
  </style>